/**
 * Comments and threads on a card.
 *
 * Reading is open (comments_for returns handles, never actor ids). Writing takes an account with
 * a handle — the same door as linking — and the server decides: 1–500 characters, no links yet,
 * on a live card, one thread level deep (a reply to a reply joins the thread), 30 an hour.
 */
import { ensureSession, supabase } from '../backend/supabase';

export interface Comment {
  id: string;
  parent_id: string | null;
  body: string;
  handle: string;
  created_at: string;
  mine: boolean;
}

export interface Thread {
  root: Comment;
  replies: Comment[];
}

/** Flat comments (oldest first) into threads, newest root first, replies in order. */
export function threadsOf(list: Comment[]): Thread[] {
  const roots = new Map<string, Thread>();
  for (const c of list) if (!c.parent_id) roots.set(c.id, { root: c, replies: [] });
  for (const c of list) {
    if (!c.parent_id) continue;
    const t = roots.get(c.parent_id);
    if (t) t.replies.push(c);
    else roots.set(c.id, { root: { ...c, parent_id: null }, replies: [] }); // parent hidden: stands alone
  }
  return [...roots.values()].sort((a, b) => (a.root.created_at < b.root.created_at ? 1 : -1));
}

export function validateComment(raw: string): { body: string } | { error: string } {
  const b = raw.replace(/\s+/g, ' ').trim();
  if (!b) return { error: 'Write something first.' };
  if (b.length > 500) return { error: `Comments are at most 500 characters — that is ${b.length}.` };
  if (/(https?:\/\/|www\.)/i.test(b)) return { error: 'Links are not allowed in comments yet.' };
  return { body: b };
}

const NO_BACKEND = 'This build has no backend, so comments are not saved.';

export async function fetchComments(cardId: string): Promise<Comment[]> {
  if (!supabase) return [];
  const { data, error } = await supabase.rpc('comments_for', { p_card_id: cardId, n: 200 });
  if (error || !Array.isArray(data)) return [];
  return data as Comment[];
}

export type PostResult = { ok: true; comment: Comment } | { ok: false; reason: string; code?: string };

export async function postComment(cardId: string, body: string, parentId: string | null): Promise<PostResult> {
  const v = validateComment(body);
  if ('error' in v) return { ok: false, reason: v.error };
  if (!supabase) return { ok: false, reason: NO_BACKEND };
  const session = await ensureSession();
  if (!session) return { ok: false, reason: 'Could not start a session — try again in a moment.' };
  const { data, error } = await supabase.rpc('post_comment', { p_card_id: cardId, p_body: v.body, p_parent: parentId });
  if (error) return { ok: false, reason: error.message || 'The server refused the comment.' };
  const r = (data ?? {}) as { ok?: boolean; comment?: Comment; reason?: string; code?: string };
  if (!r.ok || !r.comment) return { ok: false, reason: r.reason ?? 'Could not post that.', code: r.code };
  return { ok: true, comment: r.comment };
}

export async function deleteComment(id: string): Promise<boolean> {
  if (!supabase) return false;
  const { data, error } = await supabase.rpc('delete_comment', { p_id: id });
  return !error && Boolean((data as { ok?: boolean } | null)?.ok);
}
